import { useEffect, useMemo, useState } from 'react'
import { Calendar, RefreshCw, ShoppingCart, Trash2, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectOption } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useHouseholdStore } from '@/stores/household-store'
import { useMealPlanningStore } from '@/stores/meal-planning-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { ClearWeekDialog } from './clear-week-dialog'
import { MealPlanShoppingList } from './meal-plan-shopping-list'
import {
  DAY_LABELS,
  formatDateForApi,
  formatDateForDisplay,
  isRecipeMatch,
  MEAL_TYPE,
  type DayPlan,
} from './meal-planning-utils'
import { RecipePicker } from './recipe-picker'
import { SelectedDayDetail } from './selected-day-detail'
import { WeekNavigation } from './week-navigation'
import { WeekDayCard } from './week-day-card'

interface WeeklyMealPlannerProps {
  className?: string
}

export function WeeklyMealPlanner({ className }: WeeklyMealPlannerProps) {
  const {
    currentWeekStart,
    currentWeekEnd,
    currentMealPlan,
    mealPlans,
    plannedMeals,
    loading,
    loadingPlannedMeals,
    navigateToWeek,
    navigateToToday,
    loadPlannedMeals,
    addPlannedMeal,
    removePlannedMeal,
    clearWeek,
    setCurrentMealPlan,
    ensureDefaultMealPlan,
  } = useMealPlanningStore()
  const { recipes } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()

  const [selectedDateKey, setSelectedDateKey] = useState(formatDateForApi(new Date()))
  const [recipeSearch, setRecipeSearch] = useState('')
  const [showShoppingList, setShowShoppingList] = useState(false)
  const [showClearWeekDialog, setShowClearWeekDialog] = useState(false)
  const [replacingDateKey, setReplacingDateKey] = useState<string | null>(null)

  useEffect(() => {
    if (loading) {
      return
    }

    if (!currentMealPlan && mealPlans.length > 0) {
      setCurrentMealPlan(mealPlans[0])
      return
    }

    if (!currentMealPlan && mealPlans.length === 0) {
      void ensureDefaultMealPlan(viewMode === 'household' ? currentHousehold?.id : undefined)
    }
  }, [currentHousehold?.id, currentMealPlan, ensureDefaultMealPlan, loading, mealPlans, setCurrentMealPlan, viewMode])

  const weekDays = useMemo<DayPlan[]>(() => {
    const todayKey = formatDateForApi(new Date())

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(currentWeekStart)
      date.setDate(currentWeekStart.getDate() + index)
      const dateKey = formatDateForApi(date)

      return {
        date,
        dateKey,
        label: DAY_LABELS[index],
        meals: plannedMeals.filter((meal) => meal.planned_date === dateKey && meal.meal_type === MEAL_TYPE),
        isToday: dateKey === todayKey,
      }
    })
  }, [currentWeekStart, plannedMeals])

  useEffect(() => {
    if (!weekDays.length) {
      return
    }

    const selectionVisible = weekDays.some((day) => day.dateKey === selectedDateKey)
    if (!selectionVisible) {
      setSelectedDateKey(weekDays.find((day) => day.isToday)?.dateKey ?? weekDays[0].dateKey)
    }
  }, [selectedDateKey, weekDays])

  const selectedDay = weekDays.find((day) => day.dateKey === selectedDateKey) ?? weekDays[0]
  const selectedDayMeals = selectedDay?.meals ?? []

  const suggestedRecipes = useMemo(() => {
    return recipes
      .filter((recipe) => recipe.meal_type === MEAL_TYPE || !recipe.meal_type)
      .filter((recipe) => isRecipeMatch(recipeSearch, recipe.title, recipe.ingredients, recipe.tags))
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title, 'nl-NL'))
  }, [recipeSearch, recipes])

  const handlePlanRecipe = async (recipeId: string) => {
    if (!currentMealPlan || !selectedDay) {
      return
    }

    setReplacingDateKey(selectedDay.dateKey)

    for (const meal of selectedDayMeals) {
      await removePlannedMeal(meal.id)
    }

    const recipe = recipes.find((item) => item.id === recipeId)
    if (!recipe) {
      setReplacingDateKey(null)
      return
    }

    await addPlannedMeal({
      meal_plan_id: currentMealPlan.id,
      recipe_id: recipeId,
      planned_date: selectedDay.dateKey,
      meal_type: MEAL_TYPE,
      planned_servings: recipe.servings ?? null,
      notes: null,
      assigned_to: null,
    })

    setReplacingDateKey(null)
  }

  const handleClearWeek = async () => {
    await clearWeek(formatDateForApi(currentWeekStart), formatDateForApi(currentWeekEnd))
    setShowClearWeekDialog(false)
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-orange-500"></div>
          <p className="text-gray-600">Weekplanning laden...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex h-full flex-col gap-6', className)}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <WeekNavigation
          currentWeekStart={currentWeekStart}
          currentWeekEnd={currentWeekEnd}
          onNavigateToWeek={navigateToWeek}
          onNavigateToToday={navigateToToday}
          className="xl:flex-1"
        />

        <div className="flex flex-col gap-3 xl:w-[340px]">
          {mealPlans.length > 1 ? (
            <label className="space-y-1">
              <span className="text-sm font-medium text-gray-700">Actieve planning</span>
              <Select
                value={currentMealPlan?.id ?? ''}
                onChange={(event) => {
                  const selectedPlan = mealPlans.find((mealPlan) => mealPlan.id === event.target.value)
                  setCurrentMealPlan(selectedPlan ?? null)
                }}
              >
                {mealPlans.map((mealPlan) => (
                  <SelectOption key={mealPlan.id} value={mealPlan.id}>
                    {mealPlan.name} ({formatDateForDisplay(mealPlan.start_date)} - {formatDateForDisplay(mealPlan.end_date)})
                  </SelectOption>
                ))}
              </Select>
            </label>
          ) : currentMealPlan ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="text-sm font-medium text-gray-900">{currentMealPlan.name}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                <Calendar className="h-3.5 w-3.5" />
                {formatDateForDisplay(currentMealPlan.start_date)} t/m {formatDateForDisplay(currentMealPlan.end_date)}
                {currentMealPlan.household_id ? <Users className="h-3.5 w-3.5" /> : null}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              variant={showShoppingList ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setShowShoppingList((current) => !current)}
              className="flex items-center gap-2"
            >
              <ShoppingCart className="h-4 w-4" />
              Boodschappenlijst
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void loadPlannedMeals(currentMealPlan?.id ?? '')}
              disabled={!currentMealPlan || loadingPlannedMeals}
              className="flex items-center gap-2"
            >
              <RefreshCw className={cn('h-4 w-4', loadingPlannedMeals && 'animate-spin')} />
              Verversen
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowClearWeekDialog(true)}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Week wissen
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Deze week</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">
                Kies een dag om het menu te bekijken of te vervangen. Op kleinere schermen kun je horizontaal door de
                week bladeren.
              </p>

              <div className="-mx-6 overflow-x-auto px-6 pb-2">
                <div className="flex min-w-max gap-3">
                  {weekDays.map((day) => (
                    <WeekDayCard
                      key={day.dateKey}
                      day={day}
                      selected={day.dateKey === selectedDateKey}
                      onSelect={setSelectedDateKey}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <SelectedDayDetail
            selectedDay={selectedDay}
            selectedDayMeals={selectedDayMeals}
            onRemoveMeal={removePlannedMeal}
          />
        </div>

        <RecipePicker
          selectedDay={selectedDay}
          selectedDayMeals={selectedDayMeals}
          suggestedRecipes={suggestedRecipes}
          recipeSearch={recipeSearch}
          onSearchChange={setRecipeSearch}
          onPlanRecipe={handlePlanRecipe}
          loadingPlannedMeals={loadingPlannedMeals}
          replacingDateKey={replacingDateKey}
        />
      </div>

      {showShoppingList ? <MealPlanShoppingList /> : null}

      <ClearWeekDialog
        open={showClearWeekDialog}
        onOpenChange={setShowClearWeekDialog}
        onConfirm={handleClearWeek}
      />
    </div>
  )
}
